import React, { useState } from 'react';
import { ArrowLeftIcon, BriefcaseIcon, EyeIcon, PlusIcon, SaveIcon, SendIcon, Trash2Icon } from 'lucide-react';
import { CompanySettings, Material, ProCustomer, Quote, QuoteLine, QuoteStatus } from '../../types/pro';
import { QUOTE_STATUS, emptyQuoteLine, formatDate, kr, krPrecise, lineTotal, newLineId, totals } from '../../utils/pro';
import { StatusChip } from '../../components/pro/StatusChip';
interface ProQuoteEditorProps {
  quote: Quote;
  customers: ProCustomer[];
  materials: Material[];
  company: CompanySettings;
  onBack: () => void;
  onChange: (changes: Partial<Quote>) => void;
  onCreateJob: (quote: Quote) => void;
}
export function ProQuoteEditor({
  quote,
  customers,
  materials,
  company,
  onBack,
  onChange,
  onCreateJob
}: ProQuoteEditorProps) {
  const [preview, setPreview] = useState(false);
  const sums = totals(quote.lines, company.vatRate);
  const setLine = (id: string, changes: Partial<QuoteLine>) => onChange({
    lines: quote.lines.map(line => line.id === id ? {
      ...line,
      ...changes
    } : line)
  });
  const addFromMaterial = (materialId: string) => {
    const material = materials.find(m => m.id === materialId);
    if (!material) return;
    onChange({
      lines: [...quote.lines, {
        id: newLineId(),
        description: material.name,
        quantity: 1,
        unit: material.unit,
        unitPrice: material.salesPrice,
        discount: 0,
        materialId: material.id
      }]
    });
  };
  const field = 'w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
  const cell = 'w-full rounded border border-transparent bg-transparent px-2 py-1.5 text-sm outline-none hover:border-stone-200 focus:border-transparent focus:bg-white focus:ring-2 focus:ring-brand-500';
  if (preview) {
    const customer = customers.find(c => c.id === quote.customerId);
    return <div data-id="element-1235" data-mp="2mo24vdded05w">
        <button onClick={() => setPreview(false)} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-1236" data-mp="2mnsevpscqt07">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-1237" data-mp="2vk1byxsuxfj6" />
          Tilbage til redigering
        </button>

        <article className="mx-auto max-w-3xl overflow-hidden rounded-xl border border-stone-200 bg-white" data-id="element-1238" data-mp="2riz9m83n8ncz">
          <header className="flex flex-wrap items-start justify-between gap-6 border-b border-stone-200 p-8" data-id="element-1239" data-mp="1yrxprtp0a3n4">
            <div data-id="element-1240" data-mp="2crgyanlqbgzy">
              <p className="text-xl font-bold tracking-tight text-stone-900" data-id="element-1241" data-mp="2crz1y5aq0xhw">
                {company.name}
              </p>
              <p className="mt-1 text-sm leading-relaxed text-stone-500" data-id="element-1242" data-mp="27r2nixsqtsy8">
                {company.address}
                <br data-id="element-1243" data-mp="3o516u33gjym8" />
                {company.postalCode} {company.city}
                <br data-id="element-1244" data-mp="3j6rctvtgz427" />
                CVR {company.cvr}
                <br data-id="element-1245" data-mp="3rot96ygw2s40" />
                {company.phone} · {company.email}
              </p>
            </div>
            <div className="text-right" data-id="element-1246" data-mp="3vqer4y8a5wl2">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-400" data-id="element-1247" data-mp="3vr6fpp3gmu1u">
                Tilbud
              </p>
              <p className="font-mono text-lg font-bold text-stone-900" data-id="element-1248" data-mp="3hsqbq5wzfhne">
                {quote.quoteNo}
              </p>
              <p className="mt-1 text-sm text-stone-500" data-id="element-1249" data-mp="ea5clpfz666j">
                Dato {formatDate(quote.createdDate)}
                <br data-id="element-1250" data-mp="j9hyo7z0v982" />
                Gyldigt til {formatDate(quote.validUntil)}
              </p>
            </div>
          </header>

          <div className="border-b border-stone-200 p-8" data-id="element-1251" data-mp="4jamg9zlymft">
            <p className="text-xs font-semibold uppercase tracking-wider text-stone-400" data-id="element-1252" data-mp="9iwzojffs2om">
              Til
            </p>
            <p className="mt-1 text-base font-semibold text-stone-900" data-id="element-1253" data-mp="x7q28yqkd8s7">
              {quote.customerName}
            </p>
            {customer && <p className="text-sm text-stone-500" data-id="element-1254" data-mp="121au091kd6iy">
                {customer.address}, {customer.postalCode} {customer.city}
              </p>}
            <h1 className="mt-5 text-2xl font-bold tracking-tight text-stone-900" data-id="element-1255" data-mp="35go5pjzvkx3o">
              {quote.title}
            </h1>
          </div>

          <table className="w-full text-sm" data-id="element-1256" data-mp="2vj9o3h27u29s">
            <thead data-id="element-1257" data-mp="2vkbc0m4byj8x">
              <tr className="border-b border-stone-200 text-left" data-id="element-1258" data-mp="2vkumjhrni5cx">
                <th className="px-8 py-2.5 text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-1259" data-mp="3t7bhigcww6vi">
                  Beskrivelse
                </th>
                <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-1260" data-mp="3oabaa24bsy2t">
                  Antal
                </th>
                <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-1261" data-mp="3ehcfu3m5edba">
                  Pris
                </th>
                <th className="px-8 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-1262" data-mp="b0x2ee2wpqvj">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100" data-id="element-1263" data-mp="nb4tnbc1uvpy">
              {quote.lines.map(line => <tr key={line.id} data-id="element-1264" data-mp="sa7zuz3rn7zi">
                  <td className="px-8 py-2.5 text-stone-900" data-id="element-1265" data-mp="s96dhyhauz2h">
                    {line.description}
                    {line.discount > 0 && <span className="ml-2 text-xs text-emerald-600" data-id="element-1266" data-mp="11bpcu8lvmtjz">
                        −{line.discount}%
                      </span>}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2.5 text-right tabular-nums text-stone-600" data-id="element-1267" data-mp="168643bodho1a">
                    {line.quantity} {line.unit}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2.5 text-right tabular-nums text-stone-600" data-id="element-1268" data-mp="1fzk5jozvwxx2">
                    {krPrecise(line.unitPrice)}
                  </td>
                  <td className="whitespace-nowrap px-8 py-2.5 text-right font-medium tabular-nums text-stone-900" data-id="element-1269" data-mp="1p1tguh2z8vbi">
                    {krPrecise(lineTotal(line))}
                  </td>
                </tr>)}
            </tbody>
          </table>

          <div className="border-t border-stone-200 p-8" data-id="element-1270" data-mp="3utpurpcdp3p2">
            <dl className="ml-auto max-w-xs space-y-1.5 text-sm" data-id="element-1271" data-mp="3uv9a9dq7naps">
              <div className="flex justify-between" data-id="element-1272" data-mp="3q26r7zj27jnu">
                <dt className="text-stone-500" data-id="element-1273" data-mp="3q1esifu615tc">Subtotal</dt>
                <dd className="tabular-nums text-stone-900" data-id="element-1274" data-mp="8lgfilw6igcw">
                  {krPrecise(sums.subtotal)}
                </dd>
              </div>
              <div className="flex justify-between" data-id="element-1275" data-mp="hnfyvkuo1wj0">
                <dt className="text-stone-500" data-id="element-1276" data-mp="hm4tjglkogps">
                  Moms {Math.round(company.vatRate * 100)}%
                </dt>
                <dd className="tabular-nums text-stone-900" data-id="element-1277" data-mp="djqgxe8hyd6y">
                  {krPrecise(sums.vat)}
                </dd>
              </div>
              <div className="flex justify-between border-t border-stone-200 pt-1.5 text-base font-bold" data-id="element-1278" data-mp="2fdzfhffa4nln">
                <dt className="text-stone-900" data-id="element-1279" data-mp="2590ypc47043l">Total inkl. moms</dt>
                <dd className="tabular-nums text-stone-900" data-id="element-1280" data-mp="29aur6x48i1so">
                  {krPrecise(sums.total)}
                </dd>
              </div>
            </dl>

            <p className="mt-8 border-t border-stone-100 pt-5 text-xs leading-relaxed text-stone-500" data-id="element-1281" data-mp="1viy271xv480i">
              {company.quoteFooter}
            </p>
          </div>
        </article>
      </div>;
  }
  return <div data-id="element-1282" data-mp="1lnecix9w65k7">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-1283" data-mp="1lnmlie2w8qq4">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-1284" data-mp="1cmg84dvjjw13" />
        Alle tilbud
      </button>

      <header className="mb-5 flex flex-wrap items-start justify-between gap-4" data-id="element-1285" data-mp="1hkhqsxp21tc1">
        <div data-id="element-1286" data-mp="36zo3q7rccbns">
          <div className="flex items-center gap-2.5" data-id="element-1287" data-mp="36zxsxt4kaesf">
            <p className="font-mono text-xs font-semibold text-brand-700" data-id="element-1288" data-mp="3bxftow8fdnpc">
              {quote.quoteNo}
            </p>
            <StatusChip meta={QUOTE_STATUS[quote.status]} data-id="element-1289" data-mp="od0ngrcsv5rw" />
          </div>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl" data-id="element-1290" data-mp="jdxtc1ycyjyp">
            Tilbud
          </h1>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-1291" data-mp="elmsmwvsmu53">
          <button onClick={() => setPreview(true)} className="inline-flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3.5 py-2 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-1292" data-mp="alccireaenci">
            <EyeIcon className="h-4 w-4" data-id="element-1293" data-mp="llh8nhwk4bd" />
            Forhåndsvis
          </button>
          <button onClick={() => onChange({
          status: 'kladde'
        })} className="inline-flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3.5 py-2 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-1294" data-mp="3ruiwh24b6itw">
            <SaveIcon className="h-4 w-4" data-id="element-1295" data-mp="1lzzotfeft1w4" />
            Gem kladde
          </button>
          <button onClick={() => onChange({
          status: 'sendt'
        })} className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1296" data-mp="33ye444co4b8l">
            <SendIcon className="h-4 w-4" data-id="element-1297" data-mp="3doh78xdi75kn" />
            Send tilbud
          </button>
        </div>
      </header>

      {quote.status === 'accepteret' && !quote.jobId && <div className="mb-5 flex flex-wrap items-center gap-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-id="element-1298" data-mp="2l01w4lxht7el">
          <p className="min-w-0 flex-1 text-sm text-emerald-900" data-id="element-1299" data-mp="2pz64xbgi934e">
            Tilbuddet er accepteret. Opret opgaven, så linjerne følger med som
            forventede materialer.
          </p>
          <button onClick={() => onCreateJob(quote)} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700" data-id="element-1300" data-mp="2yzuhs2u5wxfp">
            <BriefcaseIcon className="h-4 w-4" data-id="element-1301" data-mp="eana3hhnta9p" />
            Opret opgave fra tilbud
          </button>
        </div>}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3" data-id="element-1302" data-mp="bki5bi4ck0f9">
        <div className="space-y-5 lg:col-span-2" data-id="element-1303" data-mp="biympkv7xdab">
          <section className="rounded-xl border border-stone-200 bg-white p-5" data-id="element-1304" data-mp="gbi28ndguihr">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-1305" data-mp="lal4y15z4cmk">
              <label className="block" data-id="element-1306" data-mp="lcxqeoaxx54d">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1307" data-mp="3jta02iea9wnv">
                  Kunde
                </span>
                <select value={quote.customerId} onChange={e => {
                const customer = customers.find(c => c.id === e.target.value);
                onChange({
                  customerId: e.target.value,
                  customerName: customer?.name ?? ''
                });
              }} className={field} data-id="element-1308" data-mp="3os3eqe5b3tpm">
                  <option value="" data-id="element-1309" data-mp="2xkb9xll7bldf">Vælg kunde…</option>
                  {customers.map(customer => <option key={customer.id} value={customer.id} data-id="element-1310" data-mp="2tjj0ar8f3egm">
                      {customer.customerNo} — {customer.name}
                    </option>)}
                </select>
              </label>
              <label className="block" data-id="element-1311" data-mp="2js552vni10ta">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1312" data-mp="2etk60zjzgn6f">
                  Overskrift
                </span>
                <input value={quote.title} onChange={e => onChange({
                title: e.target.value
              })} placeholder="f.eks. Udskiftning af eltavle" className={field} data-id="element-1313" data-mp="29tfm0zjv9abg" />
              </label>
              <label className="block" data-id="element-1314" data-mp="418wpr3o05r9">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1315" data-mp="407lnmmnfj9j">
                  Status
                </span>
                <select value={quote.status} onChange={e => onChange({
                status: e.target.value as QuoteStatus
              })} className={field} data-id="element-1316" data-mp="3v5qrwa5vyqbn">
                  {(Object.keys(QUOTE_STATUS) as QuoteStatus[]).map(key => <option key={key} value={key} data-id="element-1317" data-mp="e57fxmqkl6mm">
                      {QUOTE_STATUS[key].label}
                    </option>)}
                </select>
              </label>
              <label className="block" data-id="element-1318" data-mp="15wn4hmbot3fh">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1319" data-mp="15vbxzequxhko">
                  Gyldigt til
                </span>
                <input type="date" value={quote.validUntil} onChange={e => onChange({
                validUntil: e.target.value
              })} className={field} data-id="element-1320" data-mp="ryyfq3ei1c55" />
              </label>
            </div>
          </section>

          <section className="overflow-hidden rounded-xl border border-stone-200 bg-white" data-id="element-1321" data-mp="3hnh8g9yeiqyj">
            <header className="flex flex-wrap items-center gap-3 border-b border-stone-100 px-5 py-3.5" data-id="element-1322" data-mp="3a13ngya3ou8s">
              <h2 className="flex-1 text-base font-semibold text-stone-900" data-id="element-1323" data-mp="353ak0d06mec7">
                Tilbudslinjer
              </h2>
              <label data-id="element-1324" data-mp="3e48p6ymcwrwd">
                <span className="sr-only" data-id="element-1325" data-mp="3e3qljgxd7bef">Hent fra materialekartotek</span>
                <select value="" onChange={e => addFromMaterial(e.target.value)} className="rounded-lg border border-stone-200 px-3 py-1.5 text-xs font-medium outline-none focus:ring-2 focus:ring-brand-500" data-id="element-1326" data-mp="2qenrtapghs3k">
                  <option value="" data-id="element-1327" data-mp="306k53t045osd">Hent fra kartotek…</option>
                  {materials.map(material => <option key={material.id} value={material.id} data-id="element-1328" data-mp="2vbxtboq4du7r">
                      {material.name}
                    </option>)}
                </select>
              </label>
              <button onClick={() => onChange({
              lines: [...quote.lines, emptyQuoteLine()]
            })} className="inline-flex items-center gap-1.5 rounded-lg bg-stone-900 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-stone-800" data-id="element-1329" data-mp="22n8uxxgg82zp">
                <PlusIcon className="h-3.5 w-3.5" data-id="element-1330" data-mp="2v183fcl4qbs3" />
                Ny linje
              </button>
            </header>

            <div className="overflow-x-auto" data-id="element-1331" data-mp="2z1a17anqul23">
              <table className="w-full min-w-[640px] text-sm" data-id="element-1332" data-mp="2z4nyi4hjw42o">
                <thead data-id="element-1333" data-mp="33ztkf3aydiqr">
                  <tr className="border-b border-stone-100 bg-stone-50 text-left" data-id="element-1334" data-mp="343hd608kq14f">
                    {['Beskrivelse', 'Antal', 'Enhed', 'Stykpris', 'Rabat %', 'Total'].map(heading => <th key={heading} scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-1335" data-mp="1jkrorlhefz6a">
                        {heading}
                      </th>)}
                    <th className="w-10" data-id="element-1336" data-mp="3o4anhtpfa041" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100" data-id="element-1337" data-mp="3j5h81vqkrz18">
                  {quote.lines.map(line => <tr key={line.id} data-id="element-1338" data-mp="jnzrwamwwzpt">
                      <td className="px-2 py-1" data-id="element-1339" data-mp="jmydbw6otv3c">
                        <input value={line.description} onChange={e => setLine(line.id, {
                      description: e.target.value
                    })} placeholder="Beskrivelse" aria-label="Beskrivelse" className={cell} data-id="element-1340" data-mp="foqzpa7it9wd" />
                      </td>
                      <td className="w-20 px-2 py-1" data-id="element-1341" data-mp="2qefpdknoblcy">
                        <input type="number" value={line.quantity} onChange={e => setLine(line.id, {
                      quantity: parseFloat(e.target.value) || 0
                    })} aria-label="Antal" className={`${cell} text-right tabular-nums`} data-id="element-1342" data-mp="2rwo3huuwmqmm" />
                      </td>
                      <td className="w-20 px-2 py-1" data-id="element-1343" data-mp="2dy6jo884dy9z">
                        <input value={line.unit} onChange={e => setLine(line.id, {
                      unit: e.target.value
                    })} aria-label="Enhed" className={cell} data-id="element-1344" data-mp="2dyzfie5jj3dc" />
                      </td>
                      <td className="w-28 px-2 py-1" data-id="element-1345" data-mp="1v5c53fees86a">
                        <input type="number" value={line.unitPrice} onChange={e => setLine(line.id, {
                      unitPrice: parseFloat(e.target.value) || 0
                    })} aria-label="Stykpris" className={`${cell} text-right tabular-nums`} data-id="element-1346" data-mp="1bklm4ritjtnw" />
                      </td>
                      <td className="w-20 px-2 py-1" data-id="element-1347" data-mp="1pl63dj935hfa">
                        <input type="number" value={line.discount} onChange={e => setLine(line.id, {
                      discount: parseFloat(e.target.value) || 0
                    })} aria-label="Rabat i procent" className={`${cell} text-right tabular-nums`} data-id="element-1348" data-mp="1pijy5q9o3e08" />
                      </td>
                      <td className="whitespace-nowrap px-3 py-1 text-right font-semibold tabular-nums text-stone-900" data-id="element-1349" data-mp="3kmo74yadvdu8">
                        {kr(lineTotal(line))}
                      </td>
                      <td className="px-1" data-id="element-1350" data-mp="3pjwpozzg1jw0">
                        <button onClick={() => onChange({
                      lines: quote.lines.filter(l => l.id !== line.id)
                    })} aria-label="Slet linje" className="rounded-lg p-1.5 text-stone-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-1351" data-mp="3tmbd7xe257tm">
                          <Trash2Icon className="h-4 w-4" data-id="element-1352" data-mp="2e62qrvsrct3i" />
                        </button>
                      </td>
                    </tr>)}
                  {quote.lines.length === 0 && <tr data-id="element-1353" data-mp="2anvh0ki2z8gy">
                      <td colSpan={7} className="px-5 py-10 text-center text-sm text-stone-400" data-id="element-1354" data-mp="25pk94fbcj81d">
                        Ingen linjer endnu — tilføj en eller hent fra kartoteket.
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-xl border border-stone-200 bg-white p-5" data-id="element-1355" data-mp="1mz2lgp5df2mv">
            <label className="block" data-id="element-1356" data-mp="1mwznnixz857l">
              <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1357" data-mp="1hzhhg0bcg43a">
                Bemærkninger til kunden
              </span>
              <textarea value={quote.notes} onChange={e => onChange({
              notes: e.target.value
            })} rows={3} className={field} data-id="element-1358" data-mp="1e08sr9v5uwel" />
            </label>
          </section>
        </div>

        {/* Sums stay visible while you work, not buried at the bottom */}
        <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-1359" data-mp="xssdpezezixd">
          <div className="rounded-xl border border-stone-200 bg-white p-5" data-id="element-1360" data-mp="xtbp0cuk792f">
            <h2 className="text-base font-semibold text-stone-900" data-id="element-1361" data-mp="ssnydsa8su8q">Sum</h2>
            <dl className="mt-4 space-y-2 text-sm" data-id="element-1362" data-mp="nv46hpkmgiyb">
              <div className="flex justify-between" data-id="element-1363" data-mp="nucojhjy6jqi">
                <dt className="text-stone-500" data-id="element-1364" data-mp="jt96coefgbm4">Subtotal</dt>
                <dd className="font-medium tabular-nums text-stone-900" data-id="element-1365" data-mp="juu38ko7s6n5">
                  {krPrecise(sums.subtotal)}
                </dd>
              </div>
              <div className="flex justify-between" data-id="element-1366" data-mp="a3ze98jl2sy0">
                <dt className="text-stone-500" data-id="element-1367" data-mp="544qvez0olhp">
                  Moms {Math.round(company.vatRate * 100)}%
                </dt>
                <dd className="font-medium tabular-nums text-stone-900" data-id="element-1368" data-mp="59wdfrot8hr">
                  {krPrecise(sums.vat)}
                </dd>
              </div>
            </dl>
            <div className="mt-4 border-t border-stone-200 pt-4" data-id="element-1369" data-mp="27qs59f2ww53l">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-400" data-id="element-1370" data-mp="27qif9rhv9xxw">
                Total inkl. moms
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-stone-900" data-id="element-1371" data-mp="2grq29dexncco">
                {krPrecise(sums.total)}
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>;
}